import { useState } from 'react';
import { useCookieConsent } from '@packages/shared/src/hooks/useCookieConsent/useCookieConsent';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import type { OneTrust } from '@packages/shared/src/hooks/useCookieConsent/oneTrust';

const cookieName = 'HideRecoConsentToolCard';
const days = 10;

const personalizationGroup = 'C0003';

const getOneTrust = (): OneTrust | undefined => window?.OneTrust;

const selectPersonalizationConsentCategory = () => {
  let timeoutId: number;

  const observer = new MutationObserver(() => {
    const element = document.querySelector<HTMLElement>(`#ot-header-id-${personalizationGroup}`);

    if (element) {
      element.click();
      observer.disconnect();
      window.clearTimeout(timeoutId);
    }
  });

  // Observe the body for changes, it will be triggered when the OneTrust modal is opened.
  observer.observe(document.body, { childList: true, subtree: true });

  // Safety reason: To make sure the observer is disconnected after 2 seconds, even if there is no element.
  timeoutId = window.setTimeout(() => observer.disconnect(), 2000);
};

export const useRecoConsentTool = () => {
  const isAllowedC0003 = useCookieConsent(personalizationGroup);
  const { getCookies, setCookie } = useCookies();

  const [isSuppressed, setIsSuppressed] = useState(() => getCookies()[cookieName] === '1');

  const activateConsentTool = () => {
    const oneTrust = getOneTrust();

    if (!oneTrust?.ToggleInfoDisplay) {
      return;
    }

    selectPersonalizationConsentCategory();
    oneTrust.ToggleInfoDisplay();

    // Opened the tool, suppress for 10 days. If the user accepts, `isAllowedC0003` becomes true
    // and keeps the card hidden regardless of this cookie.
    setCookie(null, cookieName, '1', {
      path: '/',
      maxAge: days * 24 * 60 * 60,
      sameSite: 'lax',
    });
    setIsSuppressed(true);
  };

  // If there is no C0003 and no cookie suppressing the card, show it.
  return { showConsentCard: !isAllowedC0003 && !isSuppressed, activateConsentTool };
};
